import * as React from 'react';
import { useMemo } from 'react';

import { useSubfolderLanguageAndLocale } from '@coursera-web/internationalization-lib';
import type {
  DescriptionPage_ProductVariant as DescriptionPageProductVariant,
  DescriptionPage_MarketingSource as MarketingSource,
} from '__generated__/graphql-types';

import type PAGE_TYPES from 'bundles/unified-description-page-common/constants/pageTypes';
import PageInfoContext from 'bundles/unified-description-page-common/contexts/PageInfoContext';
import { getSpecializationProductUrl } from 'bundles/unified-description-page-common/utils';
import getCourseLink from 'bundles/unified-description-page-common/utils/getCourseLink';

type Props = {
  children: React.ReactNode;
  programSlug?: string;
  orgSlug?: string;
  productSlug?: string;
  marketingSource?: MarketingSource;
  pageType?: (typeof PAGE_TYPES)[keyof typeof PAGE_TYPES];
  navigationIdMap?: Record<string, string>;
  hasDubbedLanguages?: boolean;
  renderModalsInPlace?: boolean;
};

const PageInfoProvider = ({
  programSlug,
  orgSlug,
  children,
  productSlug,
  marketingSource,
  pageType,
  navigationIdMap,
  hasDubbedLanguages,
  renderModalsInPlace,
}: Props) => {
  const subfolderLocaleCode = useSubfolderLanguageAndLocale();
  const value = useMemo(
    () => ({
      getCourseLink: ({ courseSlug, specializationSlug }: { courseSlug: string; specializationSlug?: string }) =>
        getCourseLink({
          programSlug,
          orgSlug,
          courseSlug,
          specializationSlug,
          marketingSource,
          localizedLanguageRegionCode: subfolderLocaleCode,
        }),
      getSpecializationLink: ({
        productSlug: s12nProductSlug,
        productVariant,
      }: {
        productSlug: string;
        productVariant: DescriptionPageProductVariant;
      }) =>
        getSpecializationProductUrl({
          programSlug,
          orgSlug,
          productSlug: s12nProductSlug,
          productVariant,
          marketingSource,
          localizedLanguageRegionCode: subfolderLocaleCode,
        }),
      productSlug,
      orgSlug,
      pageType,
      navigationIdMap,
      marketingSource,
      subfolderLocaleCode,
      hasDubbedLanguages,
      renderModalsInPlace,
    }),
    [
      programSlug,
      orgSlug,
      productSlug,
      marketingSource,
      pageType,
      navigationIdMap,
      subfolderLocaleCode,
      hasDubbedLanguages,
      renderModalsInPlace,
    ]
  );
  return <PageInfoContext.Provider value={value}>{children}</PageInfoContext.Provider>;
};

export default PageInfoProvider;
